import React from "react";
import {
    Box,
    Paper,
    Tabs,
    Tab,
    Typography,
    Grid,
    Chip,
} from "@mui/material";

import TargetIcon from "@mui/icons-material/GpsFixed";
import BookIcon from "@mui/icons-material/MenuBook";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import default_theme from "assets/themes/themes";

const tabs = [
    { key: "aims", label: "Aims", icon: TargetIcon },
    { key: "story", label: "Story", icon: BookIcon },
    { key: "impacts", label: "Impacts", icon: TrendingUpIcon },
];

const SolutionDetailContent = ({
    prioritySections,
    otherSections,
    activeTab,
    handleTabChange,
    briefDescriptionRef,
    styles,
}) => {

    return (
        <Box className={styles.accordionContainer}>
            <Paper
                elevation={2}
                sx={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: `1px solid ${default_theme.palette.other.backgroundGrey}`,
                }}
            >
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                        borderBottom: `1px solid ${default_theme.palette.other.backgroundGrey}`,
                        "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "1rem",
                            minHeight: 64,
                            color: default_theme.palette.font.darkGray,
                            transition: "all 0.3s ease",
                            backgroundColor: default_theme.palette.primary.main,
                            "&:hover": {
                                backgroundColor: `${default_theme.palette.other.chatGreen}10`,
                            },
                        },
                        "& .Mui-selected": {
                            color: `${default_theme.palette.primary.main} !important`,
                            backgroundColor: `${default_theme.palette.other.chatGreen} !important`,
                        },
                        "& .MuiTabs-indicator": {
                            display: "none",
                        },
                    }}
                >
                    {prioritySections.map((section) => {
                        const tab = tabs.find((t) => t.key === section.key);
                        if (!tab) return null;
                        
                        const IconComponent = tab.icon;

                        return (
                            <Tab
                                key={section.key}
                                label={
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        {IconComponent && <IconComponent sx={{ fontSize: 20 }} />}
                                        <span>{section.title}</span>
                                    </Box>
                                }
                            />
                        );
                    })}
                </Tabs>

                <Box sx={{ p: 4 }}>
                    {prioritySections.map((section) => {
                        const sectionIndex = prioritySections.findIndex(
                            (s) => s.key === section.key
                        );

                        return (
                            <Box
                                key={section.key}
                                ref={section.key === "aims" ? briefDescriptionRef : null}
                                sx={{
                                    display: activeTab === sectionIndex ? "block" : "none",
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        lineHeight: 1.7,
                                        color: "text.primary",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word"
                                    }}
                                >
                                    {section.content}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Paper>

            <Paper
                elevation={2}
                sx={{
                    p: 4,
                    borderRadius: "16px",
                    border: `1px solid ${default_theme.palette.other.backgroundGrey}`,
                    backgroundColor: default_theme.palette.primary.main,
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        color: default_theme.palette.other.chatGreen,
                        mb: 3,
                    }}
                >
                    Detailed Information
                </Typography>

                <Grid container spacing={3}>
                    {otherSections
                        .filter((section) => section.key !== "status" && section.title?.toLowerCase() !== "status")
                        .map((section, sectionIndex) => {
                        const titleColor = default_theme.palette.other.chatGreen;

                        return (
                            <Grid item xs={12} md={6} key={section.key}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            color: titleColor,
                                            mb: 0.5,
                                        }}
                                    >
                                        {section.title}:
                                    </Typography>

                                    {Array.isArray(section.content) ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 1,
                                            }}
                                        >
                                            {section.content.map((value, i) => (
                                                <Chip
                                                    key={i}
                                                    label={value}
                                                    sx={{
                                                        backgroundColor:
                                                            default_theme.palette.other.backgroundGrey,
                                                        color: default_theme.palette.font.darkGray,
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    ) : section.fields ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.5,
                                            }}
                                        >
                                            {section.fields.map((field, idx) => {
                                                return (
                                                    <Box
                                                        key={idx}
                                                        sx={{ position: "relative" }}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                lineHeight: 1.6,
                                                            }}
                                                        >
                                                            <strong>{field.label}:</strong>{" "}
                                                            {field.value}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {section.content}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>
        </Box>
    );
};

export default SolutionDetailContent;